import { useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { useColors } from '../../hooks/useColors';
import { useScale } from '../../hooks/useScale';
import theme from '../../common/theme';

const LayoutSettingsStyles = () => {
  const { scale } = useScale();
  const { colors } = useColors();

  return useMemo(
    () =>
      StyleSheet.create({
        // Panel
        headingRow: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        },
        headingValue: {
          fontSize: scale(12),
          color: colors.text,
          marginBottom: scale(8),
          paddingHorizontal: scale(14),
          fontFamily: theme.fonts.medium,
        },
        cardGrid: {
          alignSelf: 'stretch',
          flexDirection: 'row',
          flexWrap: 'wrap',
          columnGap: scale(12),
          rowGap: scale(12),
        },

        // LayoutCard
        card: {
          flexGrow: 1,
          flexBasis: '45%',
          padding: scale(12),
          borderWidth: scale(2),
          borderColor: colors.borderColor,
          borderRadius: theme.radius.l,
          backgroundColor: colors.elevation1,
        },
        cardSelected: {
          borderColor: colors.green,
        },
        cardTitleRow: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: scale(12),
        },
        cardTitle: {
          fontSize: scale(14),
          color: colors.text,
          fontFamily: theme.fonts.medium,
        },
        cardDescription: {
          fontSize: scale(12),
          color: colors.textBody,
          marginTop: scale(4),
          fontFamily: theme.fonts.regular,
        },
        indicator: {
          width: scale(20),
          height: scale(20),
          alignItems: 'center',
          justifyContent: 'center',
          borderWidth: scale(2),
          borderColor: colors.textLight,
          borderRadius: scale(10),
        },
        indicatorSelected: {
          borderColor: colors.green,
          backgroundColor: colors.green,
        },

        // LayoutCard schematic preview
        preview: {
          height: scale(84),
          padding: scale(8),
          flexDirection: 'row',
          columnGap: scale(6),
          borderRadius: theme.radius.m,
          backgroundColor: colors.elevation0,
        },
        previewTopAligned: {
          alignItems: 'flex-start',
        },
        previewColumn: {
          flex: 1,
          rowGap: scale(6),
        },
        previewTile: {
          // textLight is the one neutral grey that holds up on both the light
          // and the dark inset panel; the elevation tokens invert between
          // themes and would make the tiles vanish in one of them.
          flex: 1,
          borderRadius: theme.radius.s,
          backgroundColor: colors.textLight,
        },
        // Free Flow tiles size to their contents, so each one gets its own
        // height rather than filling the column.
        flowTile0: { height: '100%' },
        flowTile1: { height: '68%' },
        flowTile2: { height: '86%' },
        flowTile3: { height: '52%' },
        flowTile4: { height: '92%' },

        // GridSizePicker
        gridCard: {
          alignSelf: 'stretch',
          flexDirection: 'row',
          padding: scale(16),
          columnGap: scale(24),
          borderRadius: theme.radius.l,
          backgroundColor: colors.elevation1,
        },
        gridControls: {
          flex: 1,
        },
        pillLabel: {
          fontSize: scale(12),
          color: colors.textBody,
          marginBottom: scale(8),
          fontFamily: theme.fonts.medium,
        },
        pillRow: {
          flexDirection: 'row',
          flexWrap: 'wrap',
          columnGap: scale(8),
          rowGap: scale(8),
          marginBottom: scale(16),
        },
        pill: {
          width: scale(40),
          height: scale(40),
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: theme.radius.m,
          backgroundColor: colors.elevation2,
        },
        pillSelected: {
          backgroundColor: colors.green,
        },
        pillDisabled: {
          opacity: 0.4,
        },
        pillText: {
          fontSize: scale(14),
          color: colors.text,
          fontFamily: theme.fonts.medium,
        },
        pillTextSelected: {
          color: colors.white,
        },
        note: {
          fontSize: scale(12),
          color: colors.textLight,
          lineHeight: scale(18),
          fontFamily: theme.fonts.regular,
        },

        // GridSizePicker live preview
        livePreview: {
          width: scale(220),
        },
        livePreviewGrid: {
          height: scale(150),
          padding: scale(8),
          rowGap: scale(6),
          borderRadius: theme.radius.m,
          backgroundColor: colors.elevation0,
        },
        livePreviewRow: {
          flex: 1,
          flexDirection: 'row',
          columnGap: scale(6),
        },
        livePreviewTile: {
          flex: 1,
          borderRadius: theme.radius.s,
          backgroundColor: colors.textLight,
        },
      }),
    [colors, scale],
  );
};

export default LayoutSettingsStyles;
